import styled from 'styled-components';
import { PropsWithChildren } from 'react';
import {
  ErrorIcon,
  Flex,
  SuccessIcon,
  useStyledTheme,
} from '@equifinance/shared';
import { ApplicationChecklist, ApplicationChecklistStatus } from '../../../contexts/applications';
import {
  BodyRow,
  GroupList,
  GroupRow,
  ChecklistOverviewContainer,
  HeadRow,
  OverviewBody,
  OverviewCell,
  OverviewHeader,
  groupItemsByStage,
} from './ExpandableContent';
import ClockIcon from '../../Icons/ClockIcon';

type StatusDisplayType = 'pending' | 'success' | 'error';

function getStatusType(status: ApplicationChecklist['status']): StatusDisplayType {
  if (['Rejected'].includes(status)) return 'error';
  if (['AwaitingDecision', 'Referred'].includes(status)) return 'pending';
  return 'success';
}

const StatusDisplay = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items: center;
  font-family: 'Montserrat';
  letter-spacing: ${theme.borderWidth[1]};
  text-transform: uppercase;
`}`;

const Icon = styled.div`${({ theme }) => `
  width: ${theme.fontSize[6]};
  height: ${theme.fontSize[6]};
  flex-shrink: 0;

  > svg {
    width: ${theme.fontSize[6]};
    height: ${theme.fontSize[6]};
  }
`}`;

function ChecklistStatusCell({ type, children }: PropsWithChildren<{ type: StatusDisplayType }>) {
  const theme = useStyledTheme();
  return (
    <StatusDisplay>
      <Icon>
        {type === 'pending' && (<ClockIcon />)}
        {type === 'error' && (
          <ErrorIcon
            fill={theme.colours.neutral[10]}
            color={theme.colours.danger[1]}
          />
        )}
        {type === 'success' && (
          <SuccessIcon
            width={theme.fontSize[6]}
            height={theme.fontSize[6]}
            fill={theme.colours.neutral[10]}
            color={theme.colours.success[1]}
          />
        )}
      </Icon>
      {children}
    </StatusDisplay>
  );
}

export default function ChecklistOverview({ checklists }: { checklists?: ApplicationChecklist[]}) {
  const groupedChecklist = groupItemsByStage(
    checklists || [],
    (checklist) => ({
      status: checklist.status,
      statusType: getStatusType(checklist.status),
      sectionName: checklist.sectionName?.trim() || '',
    }),
  );

  return (
    <ChecklistOverviewContainer>
      <OverviewHeader>Checklist overview</OverviewHeader>
      <OverviewBody>
        <HeadRow>
          <OverviewCell>UW decision</OverviewCell>
          <OverviewCell>Section name</OverviewCell>
        </HeadRow>
        <GroupList>
          {Object.entries(groupedChecklist).map(([group, checklists]) => (
            <div key={group}>
              <GroupRow>
                <OverviewCell>{group}</OverviewCell>
              </GroupRow>
              {checklists.map((checklist) => (
                <BodyRow key={checklist.sectionName + checklist.status}>
                  <OverviewCell>
                    {ApplicationChecklistStatus[checklist.status] ? (
                      <ChecklistStatusCell type={checklist.statusType}>
                        {ApplicationChecklistStatus[checklist.status]}
                      </ChecklistStatusCell>
                    ) : '-'}
                  </OverviewCell>
                  <OverviewCell>{checklist.sectionName || '-'}</OverviewCell>
                </BodyRow>
              ))}
            </div>
          ))}
        </GroupList>
      </OverviewBody>
    </ChecklistOverviewContainer>
  );
}
